/**
 * DTO types mirroring the ox-platform backend contract.
 *
 * These types are the single source of truth shared by:
 *  - The fetch client (`client.ts`)
 *  - The zod schemas (`@/lib/schemas/*`) — schemas produce these types
 *  - All feature `api` modules
 *
 * Changing a shape here means changing the contract everywhere.
 */

// ── Common ──────────────────────────────────────────────────────────────

export type Status = 'Enable' | 'Disable';

export interface ListEnvelope<T> {
  data: { items: T[]; total: number };
}

export interface BaseEntity {
  id: number;
  companyId: number | null;
  status: Status;
  createdAt: string;
  updatedAt: string;
}

// ── Auth & user ─────────────────────────────────────────────────────────

export type Role = 'admin' | 'staff' | 'viewer' | 'master';

/**
 * The backend's `User` join with `roles` returns `role` as an object `{id, name}`.
 * Legacy paths (login response, mocks) sometimes return just the role name as a
 * string. Treat both shapes as valid and use `roleLabel(user)` to render.
 */
export type RoleRef = Role | { id: number; name: string };

export interface User {
  id: number;
  email: string;
  firstname?: string;
  lastname?: string;
  phone?: string;
  picture?: string;
  companyId: number | null;
  role?: RoleRef | null;
  /** Backend returns the joined company on `/profile`. Optional. */
  company?: { id: number; name: string } | null;
  hasBrandKit?: boolean;
  /** Role within the workspace, e.g. "Brand Manager". Display-only. */
  title?: string | null;
  /** Brands this user is responsible for. Drives the "Brand managers" card. */
  managedBrandIds?: number[];
  // ── Profile-page extensions (display-only, edited from /profile) ──────
  username?: string | null;
  coverPicture?: string | null;
  location?: string | null;
  studiedAt?: string | null;
  bio?: string | null;
  interests?: string[];
  languages?: string[];
}

export interface AuthResponse {
  access_token: string;
  /**
   * Present after the backend Phase 1.3 rollout (POST /auth/refresh). Older
   * deploys only return `access_token`, so this field is optional.
   */
  refresh_token?: string;
  role?: Role;
  isNewUser?: boolean;
  user: User;
}

/**
 * Backend response from `GET /auth/onboarding-status`. The flags drive the
 * `OnboardingGate` decision tree (brand-profile / brandkit / dashboard).
 */
export interface OnboardingStatus {
  hasCompany: boolean;
  hasBrandKit: boolean;
}

// ── Console (co_) ───────────────────────────────────────────────────────

export type CompanyType = 'Company' | 'Agency';

export interface CoCompany extends BaseEntity {
  name: string;
  slug?: string;
  logo?: string | null;
  backgroundColor?: string | null;
  fontColor?: string | null;
  secondaryColor?: string | null;
  backgroundImage?: string | null;
  companyType: CompanyType;
  industry?: string | null;
  /** Top-level companies have parentId = null. Brands are children of a Company. */
  parentId?: number | null;
  /** PRD §9 — Brand Inventory opt-in per brand. Toggled from Settings › Brand. */
  brandInventoryEnabled?: boolean;
}

/**
 * A brand is a `co_companies` row of type Company nested under another company
 * via `parentId`. Branding fields are initialised from the parent company at
 * creation time but the brand owns them after (mutable, independent).
 *
 * This is a type alias for clarity at call sites — backed by the same table.
 */
export type XkBrand = CoCompany;

export interface CoCompanyAccess extends BaseEntity {
  companyId: number;
  agencyId: number;
  xotskiAccess: boolean;
  didAccess?: boolean;
  events: number;
  groups: number;
  projects: number;
  masterAdmin: number;
}

export interface CoCompanyPlanUsage extends BaseEntity {
  planId?: number;
  /** Cap on sub-brands the company can register. */
  maxBrands: number;
  maxUsers: number;
  maxRoles: number;
  maxSurveys: number;
  maxResponsesPerSurvey: number;
  /** Comma-separated list of game IDs (e.g. "wheel,scratch,trivia"). */
  allowedGames: string;
  featNotifications: boolean;
  featActiveLog: boolean;
  featUserManagement: boolean;
  featStockImport: boolean;
  featAiAssistant: boolean;
  planExpiresAt: string | null;
  /* Live counters computed by `getEffectivePlan` (COUNT(*) on users/roles/
   * surveys/sub-brands). Optional — not every endpoint populates them. */
  usersUsed?: number;
  rolesUsed?: number;
  surveysUsed?: number;
  brandsUsed?: number;
  /* Legacy fields kept optional for lingering callers; the per-app counters
   * now live on `co_company_resource_usage`. */
  projects?: number;
  events?: number;
  groups?: number;
  xotskiEvents?: number;
  xotskiEventGroups?: number;
  xotskiStations?: number;
  xotskiStocks?: number;
}

// ── Brand Kit ───────────────────────────────────────────────────────────

export type Language = 'en' | 'en-es';
export type AgeGate = 'none' | '18+' | '21+';
export type GameMechanic =
  | 'wheel'
  | 'scratch'
  | 'plinko'
  | 'pirinola'
  | 'slots'
  | 'random-draw';
export type CelebrationTheme = 'confetti' | 'fireworks' | 'sparks' | 'soccer' | 'none';

export interface BrandKit {
  campaignName: string;
  primaryColor: string;
  secondaryColor: string;
  accentColor?: string | null;
  backgroundColor?: string | null;
  fontColor?: string | null;
  font: string;
  clientLogo: string | null;
  brandLogo?: string | null;
  backgroundImage?: string | null;
  industry?: string | null;
  qrThumbnail?: string | null;
  /* Look & Feel */
  buttonStyle?: 'rounded' | 'square' | 'pill' | null;
  consentWording?: string | null;
  gameCtaEn?: string | null;
  gameCtaEs?: string | null;
  /* Form controls theming */
  inputBgColor?: string | null;
  inputBorderColor?: string | null;
  inputBorderStyle?: 'solid' | 'dashed' | 'none' | null;
  inputBorderRadius?: 'none' | 'sm' | 'md' | 'lg' | 'full' | null;
  inputTextColor?: string | null;
  labelColor?: string | null;
  /* Data */
  language: Language;
  collectData: boolean;
  collectName: boolean;
  collectEmail: boolean;
  collectPhone: boolean;
  ageGate: AgeGate;
  demoAge: boolean;
  demoGender: boolean;
  demoEthnicity: boolean;
  demoNationality: boolean;
  /* Game */
  gameMechanic: GameMechanic;
  gameTier?: 'free' | 'premium' | 'pro' | null;
  celebrationTheme: CelebrationTheme;
  /**
   * PRD §11 — Prize delivery mode (per-event, surfaced via the public
   * survey config). The Wizard skips ClaimCodeCountdown when 'off'.
   * Undefined/null = legacy back-compat → treated as 'instant'.
   */
  deliveryMode?: 'instant' | 'voucher' | 'off' | null;
  wheelTaglineEn?: string | null;
  wheelTaglineEs?: string | null;
  wheelHeroImage?: string | null;
  prizes?: BrandStudioEventPrize[] | null;
  /* Per-event runtime overrides — known shape (json column). */
  customization?: BrandStudioEventCustomization | null;
}

/**
 * PRD §0 Foundation — 3-layer Brand architecture (typed view).
 *
 * The DB stores all brand kit fields on a single `co_brandkit` row for
 * backwards compatibility. The PRD organises them into three conceptual
 * layers so reviewers and managers can reason about the kit by purpose:
 *
 *   • Identity            — visible brand expression (colors, fonts, logo)
 *   • DataCollectionPolicy — what the consumer flow asks (variant A/B,
 *                             demographics, age gate, consent wording)
 *   • ProductConfig       — operational behaviour (game mechanic, button
 *                             style, redirect mode, prizes, watermark)
 *
 * This view is generated client-side from the same BrandKit payload, so
 * the swap to a real DB-level split is one-line later (replace the
 * mapper) without touching every consumer.
 */
export interface BrandKitLayered {
  identity: {
    primaryColor?: string | null;
    secondaryColor?: string | null;
    accentColor?: string | null;
    backgroundColor?: string | null;
    fontColor?: string | null;
    font?: string | null;
    brandLogo?: string | null;
    backgroundImage?: string | null;
    industry?: string | null;
    campaignName?: string | null;
    qrThumbnail?: string | null;
  };
  dataCollectionPolicy: {
    language?: 'en' | 'es' | string | null;
    collectName?: boolean | null;
    collectEmail?: boolean | null;
    collectPhone?: boolean | null;
    ageGate?: AgeGate | null;
    demoAge?: boolean | null;
    demoGender?: boolean | null;
    demoEthnicity?: boolean | null;
    demoNationality?: boolean | null;
    consentWording?: string | null;
    consentVariant?: 'A' | 'B' | null;
  };
  productConfig: {
    buttonStyle?: 'rounded' | 'square' | 'pill' | null;
    gameMechanic?: string | null;
    gameTier?: 'free' | 'premium' | 'pro' | null;
    celebrationTheme?: CelebrationTheme | null;
    prizes?: BrandStudioEventPrize[] | null;
    postClaimRedirect?: 'none' | 'branded' | 'external' | null;
    postClaimRedirectUrl?: string | null;
    photoFilmEnabled?: boolean | null;
  };
}

/**
 * Splits a flat BrandKit-shaped payload into the 3 PRD-defined layers.
 * Pure function — safe to call in render. The reverse mapper (`flatten`)
 * isn't needed because writes still target the flat columns; only the
 * read view is layered.
 */
export function toBrandKitLayered(kit: BrandStudioEvent | (Partial<BrandStudioEvent> & { customization?: BrandStudioEventCustomization | null })): BrandKitLayered {
  return {
    identity: {
      primaryColor: kit.primaryColor,
      secondaryColor: kit.secondaryColor,
      accentColor: kit.accentColor,
      backgroundColor: kit.backgroundColor,
      fontColor: (kit as any).textColor ?? null,
      font: kit.font,
      brandLogo: kit.brandLogo,
      backgroundImage: kit.backgroundImage,
      industry: kit.industry,
      campaignName: kit.campaignName,
      qrThumbnail: kit.qrThumbnail,
    },
    dataCollectionPolicy: {
      language: kit.language ?? null,
      collectName: kit.collectName,
      collectEmail: kit.collectEmail,
      collectPhone: kit.collectPhone,
      ageGate: kit.ageGate,
      demoAge: kit.demoAge,
      demoGender: kit.demoGender,
      demoEthnicity: kit.demoEthnicity,
      demoNationality: kit.demoNationality,
      consentWording: kit.consentWording,
      consentVariant: kit.customization?.consentVariant ?? null,
    },
    productConfig: {
      buttonStyle: kit.buttonStyle,
      gameMechanic: kit.gameMechanic,
      gameTier: kit.gameTier,
      celebrationTheme: kit.celebrationTheme,
      prizes: kit.prizes,
      postClaimRedirect: kit.customization?.postClaimRedirect ?? null,
      postClaimRedirectUrl: kit.customization?.postClaimRedirectUrl ?? null,
      photoFilmEnabled: kit.customization?.photoFilmEnabled ?? null,
    },
  };
}


// ── Xotski (xk_) ────────────────────────────────────────────────────────

/** PascalCase to match the backend enum (`Active` / `Disabled` / `Finished`). */
export type EventStatus = 'Active' | 'Disabled' | 'Finished';
export type StockClassification = 'SEE' | 'ENGAGE' | 'TRY' | 'ACT';

export type ProjectStatus = 'planning' | 'live' | 'wrapped';

/**
 * EventGroup — the "Project" in UI parlance. Nests directly under a Brand.
 *   Brand ──< EventGroup (aka "Project") ──< Event ──< Station
 *
 * Note: `status` is the soft-delete flag (Enable/Disable) inherited from
 * BaseEntity. `projectStatus` is the lifecycle stage shown on cards.
 * `companyId` is the parent brand id.
 */
export interface XkEventGroup extends BaseEntity {
  name: string;
  codeRef?: string | null;
  eventCount?: number;
  projectStatus: ProjectStatus;
  startDate?: string | null;
  endDate?: string | null;
  /** Optional cover image used on the project card hero. */
  heroUrl?: string | null;
}

export interface XkEvent extends BaseEntity {
  name: string;
  address?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  codeRef?: string | null;
  sectionCount?: number;
  eventStatus: EventStatus;
  eventGroupId: number;
}

export interface XkStation extends BaseEntity {
  name: string;
  eventId: number;
  /**
   * The survey wired to this station. Each station has at most 1 survey, and
   * each survey belongs to exactly 1 station (Brand Studio creates them this way).
   */
  surveysId?: number | null;
  /**
   * When set, this station shares a QR with the rest of its group — the QR
   * resolves to `/sg/:qrGroupId` (station picker first) instead of the station's
   * own `/s/:surveyId/:stationId`.
   */
  qrGroupId?: number | null;
}

/**
 * Brand Studio config per (station, survey). All branding fields are optional
 * — when null the renderer falls back to the Brand's branding (which in turn
 * was seeded from the Company at brand creation). This is the "overrides
 * layer" sitting on top of the entity's defaults.
 */
export interface XkStationBrandConfig extends BaseEntity {
  stationId: number;
  surveyId: number;
  /** Brand identity overrides — null = inherit from Brand. */
  logo?: string | null;
  backgroundImage?: string | null;
  primaryColor?: string | null;
  secondaryColor?: string | null;
  fontColor?: string | null;
  font?: string | null;
  /** Data collection toggles (override BrandKit defaults). */
  language?: Language | null;
  collectName?: boolean | null;
  collectEmail?: boolean | null;
  collectPhone?: boolean | null;
  ageGate?: AgeGate | null;
  demoAge?: boolean | null;
  demoGender?: boolean | null;
  demoEthnicity?: boolean | null;
  demoNationality?: boolean | null;
  celebrationTheme?: CelebrationTheme | null;
  /** Campaign-level message shown on the welcome screen. */
  campaignName?: string | null;
}

// ── Brand Inventory (PRD §9, two-tier inventory model) ─────────────────

export type BrandInventoryState =
  | 'unassigned'
  | 'allocated'
  | 'distributed'
  | 'claimed'
  | 'returned'
  | 'written_off';

export const BRAND_INVENTORY_STATES: BrandInventoryState[] = [
  'unassigned',
  'allocated',
  'distributed',
  'claimed',
  'returned',
  'written_off',
];

export interface BrandInventoryItem extends BaseEntity {
  brandId: number;
  name: string;
  image?: string | null;
  quantity: number;
  estimated: boolean;
  state: BrandInventoryState;
  rating?: number | null;
  tags?: string[] | null;
  writeOffReason?: string | null;
  writtenOffAt?: string | null;
}

export interface XkStock extends BaseEntity {
  name: string;
  description?: string | null;
  /** Spanish translation of `name` shown when the survey runs in en-es. */
  translate?: string | null;
  image?: string | null;
  logo?: string | null;
  unitCost?: number;
  initialQuantity?: number;
  /**
   * PRD §9 — Brand-level units still in the brand's possession. Decremented
   * by 1 on each consumer win alongside the station's `available`. Station
   * allocations are capped against this value: the sum of active station
   * `quantity` per stockId cannot exceed `quantityRemaining`. NULL on
   * legacy rows that predate the migration (uncapped, no-op on win).
   */
  quantityRemaining?: number | null;
  /**
   * PRD §9 — Trazabilidad agency → brand. Cuando un brand adopta un xotski
   * del catálogo de su agency, la nueva fila brand-scoped guarda aquí el
   * id de la fila master. NULL para rows del agency mismo y para
   * definiciones propias del brand.
   */
  sourceStockId?: number | null;
  classification?: StockClassification | null;
  rating: number; // 1-5 rarity tier — drives wheel probability via (6 - rating)²
  restockThreshold?: number;
}

export interface XkEventStockStation extends BaseEntity {
  eventId: number;
  groupId: number | null;
  stationId: number;
  stockId: number;
  quantity: number;
  available: number;
  position?: number;
  rating?: number | null; // override
  pacingEnabled?: boolean;
}

// ── Xurvey (xy_) ────────────────────────────────────────────────────────

export type ProductType = 'xotski' | 'xamplin' | 'xeckin' | 'rs1' | 'rs2';

export type XyComponentType =
  | 'inputText'
  | 'inputNumber'
  | 'inputDate'
  | 'numberRange'
  | 'uploadPhoto'
  | 'selectPhoto'
  | 'check'
  | 'singleCheck'
  | 'switchCheck'
  | 'select'
  | 'tagSelect'
  | 'languageSelector'
  | 'welcomeScreen'
  | 'consentForm'
  | 'completionScreen'
  | 'gameWheel'
  | 'gameScratch'
  | 'gamePlinko'
  | 'gamePirinola'
  | 'gameSlots'
  | 'gameRandomDraw'
  | 'gameSoccer'
  | 'gameLoteria'
  | 'descriptionText'
  | 'specialType';

// ── Games (g_game + co_company_game) ───────────────────────────────────

export type GameTier = 'free' | 'premium' | 'pro';
export type GameSource = 'default' | 'granted' | 'purchased';

/** Catalog row — global definition of a mini-game. */
export interface GameDef {
  id: number;
  code: string;             // 'wheel', 'scratch', …
  name: string;
  description?: string | null;
  tier: GameTier;
  previewImageUrl?: string | null;
  componentType: string;    // 'wheelComponent', 'scratchComponent', …
  isActive: number;
}

/** Catalog row enriched with caller-company entitlement metadata. */
export interface AvailableGame extends GameDef {
  entitled: boolean;
  source?: GameSource;
  expiresAt?: string | null;
}

/** Single option for select/singleCheck/check/tagSelect components. */
export interface XyComponentOption {
  key: string;
  value: string;
  translate?: string;
}

export interface XyComponent extends BaseEntity {
  stepId: number;
  type: XyComponentType;
  label: string;
  labelTranslate?: string | null;
  /** Legacy alias for `initialization`; backend column is `initialization` (JSON). */
  options?: Record<string, unknown> | null;
  /** Source of truth for select/singleCheck/check/tagSelect options. */
  initialization?: XyComponentOption[] | Record<string, unknown> | null;
  validation?: Array<{ key: string; value: string }> | Record<string, unknown> | null;
  /** Discriminator for specialType (e.g. 'consentStandard', 'wheelComponent'). */
  specialField?: string | null;
  /**
   * Phase 4: multi-language overrides. Shape:
   *   { es: { label, options: [{key, value}, ...] }, pt: {...}, fr: {...} }
   * Read priority at render time:
   *   translations[lang].label ?? labelTranslate ?? label
   */
  translations?: Record<string, {
    label?: string;
    options?: XyComponentOption[];
  }> | null;
}

export interface XyStep extends BaseEntity {
  surveyId: number;
  name: string;
  nameTranslate?: string | null;
  description?: string | null;
  descriptionTranslate?: string | null;
  footerText?: string | null;
  nextStep?: number | null;
  skipTo?: number | null;
  components?: XyComponent[];
  /** Phase 4: multi-language overrides. { es: { name, description, footerText }, pt: {...} } */
  translations?: Record<string, {
    name?: string;
    description?: string;
    footerText?: string;
  }> | null;
}

export interface XySurvey extends BaseEntity {
  name: string;
  description?: string | null;
  numberOfSteps: number;
  backgroundImage?: string | null;
  backgroundColor?: string | null;
  borderColor?: string | null;
  primaryColor?: string | null;
  logo?: string | null;
  endpointUrl?: string | null;
  isTranslatable: boolean;
  productType?: ProductType | null;
  isLocked: boolean;
  isTemplate: boolean;
  sourceTemplateId?: number | null;
  surveysObject?: Record<string, unknown> | null;
  steps?: XyStep[];
  // Caso 06 / 08 — multi-language. `languages` lists the published
  // codes; `defaultLanguage` is the fallback locale.
  languages?: string[] | null;
  defaultLanguage?: string;
}

export interface XySurveyResponseItem {
  stepId: number;
  componentId: number;
  value: string;
}

/**
 * Per-event snapshot of brand identity + xurvey customization.
 * 1:1 with XkEvent. Created when the manager picks a template for the event.
 * Fields are nullable: null means "inherit from CoBrandKit" at runtime.
 */
export interface BrandStudioEventPrize {
  id: string;
  name: string;
  rating: number;
  quantity: number;
}

export interface BrandStudioEventCustomization {
  disabledComponentIds?: number[];
  componentLabelOverrides?: Record<number, { en?: string; es?: string }>;
  // PRD §11 Screen 3 — consent flow variant.
  //   'A' = anonymous (no PII, no marketing checkbox)
  //   'B' = PII + unbundled marketing opt-in (default)
  consentVariant?: 'A' | 'B';
  // PRD §11 Screen 6 — post-claim redirect mode.
  //   'none'     = land on the Onxite finish screen (default)
  //   'branded'  = stay on the survey, show a branded thank-you panel
  //   'external' = redirect the browser to postClaimRedirectUrl
  postClaimRedirect?: 'none' | 'branded' | 'external';
  postClaimRedirectUrl?: string;
  // PRD §11 Screen 1.75 — Photo & Film Notice. When true, the consumer
  // flow renders the notice screen between the LDA age gate and the
  // first survey step. Default false (events without media coverage).
  photoFilmEnabled?: boolean;
}

export interface BrandStudioEvent extends BaseEntity {
  eventId: number;
  surveyId: number | null;
  sourceTemplateId: number | null;
  // Identity snapshot
  primaryColor: string | null;
  secondaryColor: string | null;
  accentColor: string | null;
  backgroundColor: string | null;
  textColor: string | null;
  font: string | null;
  campaignName: string | null;
  brandLogo: string | null;
  clientLogo: string | null;
  backgroundImage: string | null;
  industry: string | null;
  qrThumbnail: string | null;
  // Look & Feel
  buttonStyle: 'rounded' | 'square' | 'pill' | null;
  consentWording: string | null;
  gameCtaEn: string | null;
  gameCtaEs: string | null;
  // Form-controls theming (applied to inputs/selects/pills/toggles)
  inputBgColor: string | null;
  inputBorderColor: string | null;
  inputBorderStyle: 'solid' | 'dashed' | 'none' | null;
  inputBorderRadius: 'none' | 'sm' | 'md' | 'lg' | 'full' | null;
  inputTextColor: string | null;
  labelColor: string | null;
  // Experience
  language: Language | null;
  collectName: boolean | null;
  collectEmail: boolean | null;
  collectPhone: boolean | null;
  ageGate: AgeGate | null;
  demoAge: boolean | null;
  demoGender: boolean | null;
  demoEthnicity: boolean | null;
  demoNationality: boolean | null;
  // Game
  gameMechanic: string | null;
  gameTier: 'free' | 'premium' | 'pro' | null;
  celebrationTheme: CelebrationTheme | null;
  /**
   * PRD §11 — Prize delivery mode per event:
   *   'instant' (default) — 5-min claim code + QR + BA canjeo.
   *   'voucher'           — 7-day code, same canjeo UI.
   *   'off'               — no claim code; BA hands prize directly,
   *                          no Xotski tracking.
   * null = legacy back-compat → treated as 'instant'.
   */
  deliveryMode: 'instant' | 'voucher' | 'off' | null;
  wheelTaglineEn: string | null;
  wheelTaglineEs: string | null;
  wheelHeroImage: string | null;
  // Prizes
  prizes: BrandStudioEventPrize[] | null;
  // Free-form per-event customization. Known shape:
  //   disabledComponentIds: number[] — xy_component ids hidden in this event
  //   componentLabelOverrides: Record<id, { en?, es? }>  (future)
  customization: BrandStudioEventCustomization | null;
  // Joined (when fetched with ?withRelations=true)
  survey?: XySurvey | null;
  event?: XkEvent | null;
}

/**
 * PRD §11 Screen 1.5 — Legal Drinking Age gate.
 *
 * When the survey's brand kit configures an `ageGate` ('18+' / '21+'), the
 * consumer flow presents a yes/no prompt before the first step. The FE
 * captures `Date.now()` the moment the consumer taps "Yes" and forwards it
 * here so the backend can persist `ldaVerifiedAt` on `xk_prize_redemptions`
 * for compliance audits. Per PRD §13, we never store the DOB — only the
 * timestamp of the verification act.
 */
export interface XySurveyResponseBulk {
  surveyId: number;
  station: number;
  source: ProductType;
  date: string; // ISO
  stockId?: number;
  responses: XySurveyResponseItem[];
  /**
   * ISO timestamp captured when the consumer affirmed the legal drinking-age
   * prompt (PRD §11 Screen 1.5). Omitted when the kit has `ageGate: 'none'`.
   * Backend writes this to `xk_prize_redemptions.ldaVerifiedAt`.
   */
  ldaVerifiedAt?: string;
}

// ── Ecommerce (ec_) ─────────────────────────────────────────────────────

/** @deprecated legacy plan tier; the real backend identifies plans by `slug`. */
export type PlanTier = 'free' | 'starter' | 'pro' | 'agency';
export type PlanType = 'STANDARD' | 'ADDITIONAL';
export type PlanBillingPeriod = 'month' | 'year';

/**
 * Mirrors the real `ec_plan` row returned by `/api/v1/plans/catalog`.
 * Numeric DECIMAL columns are serialized as strings by TypeORM — keep both
 * shapes accepted in the type so cards can parseFloat without surprises.
 */
export interface EcPlan extends BaseEntity {
  name: string;
  slug: string;
  /** Owning app (xotski/xamplin/console/did) or NULL for global SKUs. */
  app: string | null;
  /** Stripe subscription cadence. Drives the displayed "/month" or "/year". */
  pricePeriod: PlanBillingPeriod;
  /** Total billed per period (e.g. 1500 for Pilot yearly). NOTE: TypeORM
   * DECIMAL columns serialise as strings over the wire; consumers should
   * `Number(plan.value)` before doing math. */
  value: number;
  /** Pre-computed monthly equivalent (same string-vs-number caveat). */
  priceMonthly: number;
  /** Stripe wiring — null means "Talk to us" tier (no Checkout). */
  stripePriceId: string | null;
  stripeProductId: string | null;
  /* Caps surfaced on pricing cards */
  events: number;
  maxBrands: number;
  maxUsers: number;
  maxXotskies: number;
  /* Other quota fields (used elsewhere) */
  projects: number;
  groups: number;
  /* Feature flags */
  allowedGames: string;
  featAiAssistant: boolean;
  featNotifications: boolean;
  featActiveLog: boolean;
  featUserManagement: boolean;
  featStockImport: boolean;
  /* Legacy optional fields, not part of the canonical catalog row. */
  tier?: PlanTier;
  features?: string[];
  discount?: number;
  discountCode?: string | null;
  typePlan?: PlanType;
  isAccumulative?: boolean;
  /* Xotski-specific caps that live on the usage view, not on the catalog row.
   * Kept here as optional for callers that copy caps onto plan rows. */
  xotskiEvents?: number;
  xotskiEventGroups?: number;
  xotskiStations?: number;
  xotskiStocks?: number;
}

/** Lifecycle of a purchased plan invoice (backend `InvoicePlanStatus` enum). */
export type InvoicePlanStatus = 'Active' | 'Expired' | 'Refunded' | 'Exhausted';

export interface EcInvoicePlan extends BaseEntity {
  planId: number;
  amount: number;
  /** Plan lifecycle status (NOT NULL on the API). */
  planStatus: InvoicePlanStatus;
  /** Joined plan row — present when the API eager-loads the relation. */
  plan?: EcPlan | null;
  stripeSessionId?: string | null;
}

export interface StripeCheckoutSession {
  url: string;
  sessionId: string;
}

// ── Public survey object response ───────────────────────────────────────
//
// Mirrors the backend `PublicSurveyResponseDto` shape (see
// ox-platform/src/apps/xurvey/dto/public-survey-response.dto.ts).
// The response is FLAT — no `survey: {}` wrapper. Consumers read top-level
// fields directly: data.id, data.steps, data.stocks, data.brandKit.
//
// `apiVersion` is the contract version (currently 1). When the backend
// bumps it, frontends should refuse to render or warn.

// ── Caso 01 — Leads CRM ─────────────────────────────────────────────────────

/**
 * One row in `co_contacts`. The backend dedupes by
 * `(companyId, emailNormalized)` then by phone — see
 * `docs-pend/01-leads-crm/PLAN-TECNICO.md`.
 */
export interface CoContact {
  id: number;
  companyId: number;
  status: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  emailNormalized: string | null;
  phoneNormalized: string | null;
  marketingConsent: boolean;
  demographics: Record<string, string> | null;
  firstSeenAt: string | null;
  lastSeenAt: string | null;
  interactionsCount: number;
  prizesWonCount: number;
  tags: string[] | null;
  createdAt: string;
  updatedAt: string;
}

/** One survey submission tied to a `CoContact`. */
export interface CoContactInteraction {
  id: number;
  contactId: number;
  responseId: string;
  surveyId: number | null;
  xkEventId: number | null;
  xkStationId: number | null;
  source: string | null;
  prizeStockId: number | null;
  submittedAt: string | null;
  demographics: Record<string, string> | null;
}

export interface CoContactWithInteractions extends CoContact {
  interactions: CoContactInteraction[];
}

export interface CoContactListResponse {
  items: CoContact[];
  total: number;
  page: number;
  limit: number;
}

export interface CoContactFilters {
  q?: string;
  eventId?: number;
  brandId?: number;
  from?: string;
  to?: string;
  consent?: boolean;
  page?: number;
  limit?: number;
}

export interface UpdateCoContactPayload {
  firstName?: string;
  lastName?: string;
  marketingConsent?: boolean;
  tags?: string[];
}

export interface PublicSurveyObject {
  apiVersion: number;
  id: number;
  name: string;
  description?: string;
  numberOfSteps?: number;
  backgroundImage?: string | null;
  backgroundColor?: string | null;
  logo?: string | null;
  endpointUrl: string;
  isTranslatable: boolean;
  // Caso 06 — multi-language. Optional so legacy backends keep compiling.
  // `languages` lists the codes the survey is available in (e.g.
  // ['en', 'es']); NULL means single-language. `defaultLanguage` is the
  // fallback used by the renderer when a translation entry is missing.
  languages?: string[] | null;
  defaultLanguage?: string;
  productType?: string | null;
  surveysObject?: Array<{ key: string; value: string }>;
  company?: PublicSurveyCompany | null;
  brandKit?: BrandKit | null;
  stocks: (XkStock & { available: number; rating: number; position: number; eventStockGroupId?: number })[];
  steps: (XyStep & { components: XyComponent[] })[];
  // Caso 15: when a plan-cap or admin action locks the survey, the backend
  // short-circuits the public payload — only `id`, `name`, `status`,
  // `lockedReason`, `responsesCount` are guaranteed. The runner pivots to
  // <SurveyLockedScreen> before reading any of the other (then-missing)
  // fields, so we keep the unlocked shape required and gate access at the
  // call site.
  lockedReason?: 'LIMIT_EXHAUSTED' | 'PLAN_EXPIRED' | 'MANUAL_DISABLE' | null;
  status?: string;
  responsesCount?: number;
}

// ── Station Summary (GET /xk-station/:id/summary) ────────────────────
//
// Mirrors backend `XkStationSummaryDto` (ox-platform/.../dto/xk-station-summary.dto.ts).
// Powers the Station Detail page (header + 3 tabs) and the AI assistant
// Session context. The response analytics block is generic and reused by
// xamplin/did equivalents at the backend (via TouchpointInsightsService).

export interface StationSummaryStation {
  id: number;
  name: string;
  status: string;
  companyId: number;
  eventId: number;
  surveysId: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface StationSummaryEvent {
  id: number;
  name: string;
  codeRef: string | null;
  address: string | null;
  startDate: string | null;
  endDate: string | null;
  status: string;
  eventGroupId: number;
}

export interface StationSummarySurvey {
  id: number;
  name: string;
  productType: string | null;
  isTemplate: boolean;
  isLocked: boolean;
}

export interface StationInventoryItem {
  id: number;
  stockId: number;
  stockName: string;
  stockImage: string | null;
  rating: number | null;
  quantity: number;
  available: number;
  used: number;
  position: number | null;
  pacingEnabled: boolean;
  lastWonAt: string | null;
}

export interface StationInventoryTotals {
  items: number;
  quantity: number;
  used: number;
  available: number;
}

export interface TouchpointTimeSeriesBucket {
  bucket: string;
  count: number;
}

export interface TouchpointTopAnswerOption {
  value: string;
  count: number;
}

export interface TouchpointTopAnswerComponent {
  componentId: number;
  componentLabel: string;
  componentType: string;
  options: TouchpointTopAnswerOption[];
}

export interface TouchpointResponseAnalytics {
  totalResponses: number;
  firstAt: string | null;
  lastAt: string | null;
  bucket: 'hour' | 'day';
  timeSeries: TouchpointTimeSeriesBucket[];
  topAnswers: TouchpointTopAnswerComponent[];
  last24hCount: number;
  last7dCount: number;
}

export interface XkStationSummary {
  station: StationSummaryStation;
  event: StationSummaryEvent | null;
  survey: StationSummarySurvey | null;
  inventory: StationInventoryItem[];
  inventoryTotals: StationInventoryTotals;
  responses: TouchpointResponseAnalytics;
  /**
   * Survey ids that have responses at this station, ordered most-recent
   * first. Use index 0 when drilling into individual responses (the active
   * `station.surveysId` may point to a freshly-cloned empty survey).
   */
  responseSurveyIds: number[];
  qr: { shortUrl: string | null };
}

export interface PublicSurveyCompany {
  id: number;
  name: string;
  logo?: string | null;
  backgroundColor?: string | null;
  backgroundImage?: string | null;
  secondaryColor?: string | null;
  fontColor?: string | null;
  celebrationTheme?: string | null;
}
